/**
 * Block zero, and what it handed out.
 *
 * Endowments happen here and are invisible to anyone who has not read the
 * chainspec — there is no extrinsic for them, no event, nothing in the ordinary
 * record. They exist only as balances in block zero's state.
 *
 * Which is why this is attached to the block page rather than filed somewhere a
 * reader would have to think to look: whoever opens block zero is exactly the
 * person who should see who the chain started with.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { GenesisDetail } from '../api/generated/GenesisDetail'
import { RequestFailed, fetchGenesis } from '../api/rest'
import { exactTokens, shortAddress, tokens, when } from '../lib/format'
import { href } from '../lib/routes'

export function GenesisPanel({
  chain,
  decimals,
  symbol,
}: {
  chain: string
  decimals: number
  symbol: string
}) {
  const [detail, setDetail] = useState<GenesisDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setDetail(null)
    setError(null)
    fetchGenesis(chain, controller.signal)
      .then(setDetail)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <div>
          <div className="eyebrow">Genesis</div>
          <h2 className="panel-title" style={{ marginTop: 4 }}>
            Who this chain started with
          </h2>
          {detail?.hash && (
            <dl className="identifiers">
              <dt>Genesis hash</dt>
              <dd className="numeral">{detail.hash}</dd>
              {detail.started_at && (
                <>
                  <dt>Started</dt>
                  <dd className="numeral">{when(detail.started_at)}</dd>
                </>
              )}
            </dl>
          )}
        </div>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && detail === null && <p className="empty">Reading block zero…</p>}

      {detail !== null && !detail.readable && (
        /* Missing data, not an absence of endowment. Genesis state lives only
           on an archive node, and a page that showed an empty table here would
           be asserting something it cannot know. */
        <p className="empty">
          Block zero's state is not available from this node. Endowments live only there, so they
          cannot be shown — this is missing data rather than a chain that endowed nobody.
        </p>
      )}

      {detail !== null && detail.readable && (
        <>
          <div className="stats" style={{ margin: 0, border: 0, borderBottom: 'var(--rule)' }}>
            <div className="stat">
              <div className="stat-label">Endowed accounts</div>
              <div className="stat-value">{detail.endowed.length}</div>
              <div className="stat-note">held a balance in block zero</div>
            </div>
            <div className="stat">
              <div className="stat-label">Handed out</div>
              <div
                className="stat-value"
                title={`${exactTokens(detail.total_endowed, decimals)} ${symbol}`}
              >
                {tokens(detail.total_endowed, decimals, 2)}
              </div>
              <div className="stat-note">{symbol} at genesis</div>
            </div>
          </div>

          {detail.endowed.length === 0 ? (
            <p className="empty">No account held a balance in block zero.</p>
          ) : (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">
                  Accounts endowed at genesis, largest first
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      Account
                    </th>
                    <th scope="col">Endowed</th>
                    <th scope="col">Holds now</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.endowed.map((a) => {
                    const endowment = a.roles[0]?.endowment ?? null
                    return (
                      <tr key={a.account}>
                        <td className="left">
                          <Link className="numeral" to={href({ chain, account: a.address })}>
                            {shortAddress(a.address)}
                          </Link>
                        </td>
                        <td className="numeral">
                          {endowment ? `${tokens(endowment, decimals)} ${symbol}` : '—'}
                        </td>
                        <td className="numeral">
                          {/* Then against now. An endowed account that is empty
                              spent it; one that has grown has been earning. */}
                          {a.balance === null ? (
                            <span className="runtime-modifier">gone</span>
                          ) : (
                            `${tokens(a.balance, decimals)} ${symbol}`
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="panel-note">
            Read from <code>System::Account</code> in block zero's own state. There is no extrinsic
            for an endowment and no event — it is simply a balance the chain was born holding, which
            is why it is invisible to anyone who has not read the chainspec. <em>Holds now</em> is
            the same account today, so the two columns together say what each founding account did
            with what it was given.
          </p>
        </>
      )}
    </section>
  )
}
